import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, getApiError } from '../../lib/api';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { Money } from '../../components/Money';
import { StatusPill } from '../../components/StatusPill';
import { Modal } from '../../components/Modal';
import { EmptyState } from '../../components/EmptyState';
import { Pagination } from '../../components/Pagination';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';
import { cn } from '../../lib/cn';
import { ProductImagesModal } from './ProductImagesModal';
import { ProductImagesManager } from './ProductImagesManager';
import { ProductModelWizard } from './ProductModelWizard';
import { ModelImagesManager } from '../sales/ModelImagesManager';
import { VatPricePair } from '../../components/VatPricePair';
import { uploadProductImage } from '../../lib/upload';
import { useHasPermission } from '../../lib/permissions';

const PAGE_SIZE = 25;

interface Product {
  id: string;
  modelId: string | null;
  sku: string;
  name: string;
  description: string | null;
  basePrice: string;
  salePrice: string | null;
  vatRate: string;
  isActive: boolean;
  needsPricing: boolean;
  category: { id: string; name: string; code: string };
  images: { id: string; url: string; isPrimary: boolean }[];
}

interface ModelApi {
  id: string;
  name: string;
  category: { id: string; name: string; code: string };
  image: string | null;
  variantCount: number;
  priceFrom: string | null;
  priceTo: string | null;
  totalStock: number | null;
  stockTracked: boolean;
  isActive: boolean;
  needsPricing: boolean;
  variants: { id: string; salePrice: string | null }[];
}

// A unified table row — either a grouped model or a standalone product.
type Row =
  | {
      kind: 'model';
      id: string;
      name: string;
      category: { id: string; name: string; code: string };
      image: string | null;
      isActive: boolean;
      needsPricing: boolean;
      onSale: boolean;
      price: number;
      model: ModelApi;
    }
  | {
      kind: 'product';
      id: string;
      name: string;
      category: { id: string; name: string; code: string };
      image: string | null;
      isActive: boolean;
      needsPricing: boolean;
      onSale: boolean;
      price: number;
      product: Product;
    };

interface Category {
  id: string;
  name: string;
  code: string;
  fulfillmentStrategy?: string;
  tracksStockPerLocation?: boolean;
}

type StatusFilter = 'all' | 'active' | 'inactive';
type SortKey = 'name_asc' | 'name_desc' | 'sku_asc' | 'price_asc' | 'price_desc';

export function ProductsPage(): JSX.Element {
  const [createOpen, setCreateOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [resumeId, setResumeId] = useState<string | null>(null);
  const [editModelId, setEditModelId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Product | null>(null);
  const [productImagesFor, setProductImagesFor] = useState<string | null>(null);
  const [modelImagesFor, setModelImagesFor] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [onSaleOnly, setOnSaleOnly] = useState(false);
  const [needsPricingOnly, setNeedsPricingOnly] = useState(false);
  const [sort, setSort] = useState<SortKey>('name_asc');
  const [page, setPage] = useState(1);
  const qc = useQueryClient();

  const models = useQuery({
    queryKey: ['product-models', 'admin'],
    queryFn: () => apiGet<ModelApi[]>('/product-models', { includeInactive: 'true' }),
  });
  const products = useQuery({
    queryKey: ['products-admin'],
    queryFn: () => apiGet<Product[]>('/products', { includeInactive: 'true' }),
  });
  const cats = useQuery({
    queryKey: ['cats-admin-min'],
    queryFn: () => apiGet<Category[]>('/categories'),
  });
  // Models created in the wizard but not yet given any sizes — offer to finish them.
  const incomplete = useQuery({
    queryKey: ['product-models-incomplete'],
    queryFn: () =>
      apiGet<Array<{ id: string; name: string; category: { name: string } }>>('/product-models', {
        incomplete: 'true',
      }),
  });
  const deleteDraft = useMutation({
    mutationFn: (id: string) => apiDelete(`/product-models/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-models-incomplete'] }),
  });

  const refreshProducts = (): void => {
    void qc.invalidateQueries({ queryKey: ['products-admin'] });
  };
  const refreshModels = (): void => {
    void qc.invalidateQueries({ queryKey: ['product-models'] });
  };
  const deactivateProduct = useMutation({
    mutationFn: (id: string) => apiPost(`/products/${id}/deactivate`),
    onSuccess: refreshProducts,
  });
  const reactivateProduct = useMutation({
    mutationFn: (id: string) => apiPatch(`/products/${id}`, { isActive: true }),
    onSuccess: refreshProducts,
  });
  const deactivateModel = useMutation({
    mutationFn: (id: string) => apiPost(`/product-models/${id}/deactivate`),
    onSuccess: refreshModels,
  });
  const reactivateModel = useMutation({
    mutationFn: (id: string) => apiPatch(`/product-models/${id}`, { isActive: true }),
    onSuccess: refreshModels,
  });

  // Unified rows: one per model + one per standalone product (no model).
  const rows = useMemo<Row[]>(() => {
    const modelRows: Row[] = (models.data ?? []).map((m) => ({
      kind: 'model',
      id: m.id,
      name: m.name,
      category: { id: m.category.id, name: m.category.name, code: m.category.code },
      image: m.image,
      isActive: m.isActive,
      needsPricing: m.needsPricing,
      onSale: m.variants.some((v) => v.salePrice),
      price: Number(m.priceFrom ?? 0),
      model: m,
    }));
    const productRows: Row[] = (products.data ?? [])
      .filter((p) => p.modelId == null)
      .map((p) => ({
        kind: 'product',
        id: p.id,
        name: p.name,
        category: { id: p.category.id, name: p.category.name, code: p.category.code },
        image: (p.images.find((i) => i.isPrimary) ?? p.images[0])?.url ?? null,
        isActive: p.isActive,
        needsPricing: p.needsPricing,
        onSale: !!p.salePrice,
        price: Number(p.salePrice ?? p.basePrice),
        product: p,
      }));
    return [...modelRows, ...productRows];
  }, [models.data, products.data]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const out = rows.filter((r) => {
      if (categoryId && r.category.id !== categoryId) return false;
      if (status === 'active' && !r.isActive) return false;
      if (status === 'inactive' && r.isActive) return false;
      if (onSaleOnly && !r.onSale) return false;
      if (needsPricingOnly && !r.needsPricing) return false;
      if (ql) {
        const hay = r.kind === 'product' ? `${r.name} ${r.product.sku}` : r.name;
        if (!hay.toLowerCase().includes(ql)) return false;
      }
      return true;
    });
    out.sort((a, b) =>
      sort === 'name_desc'
        ? b.name.localeCompare(a.name)
        : sort === 'price_asc'
          ? a.price - b.price
          : sort === 'price_desc'
            ? b.price - a.price
            : a.name.localeCompare(b.name),
    );
    return out;
  }, [rows, q, categoryId, status, onSaleOnly, needsPricingOnly, sort]);

  const needsPricingCount = useMemo(() => rows.filter((r) => r.needsPricing).length, [rows]);
  const hasFilters = !!(
    q ||
    categoryId ||
    status !== 'all' ||
    onSaleOnly ||
    needsPricingOnly ||
    sort !== 'name_asc'
  );
  const clearFilters = (): void => {
    setQ('');
    setCategoryId('');
    setStatus('all');
    setOnSaleOnly(false);
    setNeedsPricingOnly(false);
    setSort('name_asc');
  };

  useEffect(() => {
    setPage(1);
  }, [q, categoryId, status, onSaleOnly, needsPricingOnly, sort]);
  const loading = models.isLoading || products.isLoading;
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <PageScaffold
      scroll={false}
      title="Products"
      subtitle={
        <>
          {rows.length} item{rows.length === 1 ? '' : 's'} in catalogue
          {filtered.length !== rows.length && ` · ${filtered.length} match`}.
        </>
      }
      actions={
        <>
          <Button variant="secondary" onClick={() => setCreateOpen(true)}>
            + Single product
          </Button>
          <Button onClick={() => setWizardOpen(true)}>+ New model (with sizes)</Button>
        </>
      }
    >
      {/* Slim incomplete-models alert strip */}
      {(incomplete.data ?? []).length > 0 && (
        <div className="shrink-0 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-warning/40 bg-warning/5 px-4 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-warning shrink-0">
            Needs setup
          </span>
          {(incomplete.data ?? []).map((m) => (
            <span key={m.id} className="flex items-center gap-2">
              <span className="text-sm text-ink">
                {m.name}
                <span className="ml-1 text-xs text-ink-4">· {m.category.name}</span>
              </span>
              <Button size="sm" onClick={() => setResumeId(m.id)}>
                Continue →
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={deleteDraft.isPending}
                onClick={() => deleteDraft.mutate(m.id)}
              >
                Delete
              </Button>
            </span>
          ))}
        </div>
      )}

      {/* Filter panel — a dedicated search/sort top bar over the chip filters,
          so the controls breathe and gutters line up with the table below. */}
      <div className="shrink-0 rounded-lg border border-rule bg-surface shadow-sm">
        {/* Top bar: search · sort · clear · result count */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-rule-2">
          <div className="relative w-full sm:w-72">
            <svg
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-4"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              className="kc-input h-9 w-full pl-8 text-sm"
              placeholder="Search name or SKU…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-ink-3">
            <span className="uppercase tracking-wide">Sort</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              className="kc-input h-9 w-36 text-sm"
            >
              <option value="name_asc">Name A → Z</option>
              <option value="name_desc">Name Z → A</option>
              <option value="price_asc">Price low → high</option>
              <option value="price_desc">Price high → low</option>
            </select>
          </label>
          {hasFilters && (
            <Button variant="tertiary" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
          <span className="ml-auto text-xs text-ink-3 whitespace-nowrap">
            <span className="font-medium text-ink">{filtered.length}</span>
            {rows.length !== filtered.length && ` of ${rows.length}`}{' '}
            item{filtered.length === 1 ? '' : 's'}
          </span>
        </div>

        {/* Chip filters: category, then status + toggles */}
        <div className="space-y-2 px-4 py-2.5">
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="w-14 shrink-0 text-[10px] uppercase tracking-widest text-ink-4">
              Category
            </span>
            <Chip sm active={categoryId === ''} onClick={() => setCategoryId('')}>
              All
            </Chip>
            {(cats.data ?? []).map((c) => (
              <Chip
                key={c.id}
                sm
                active={categoryId === c.id}
                onClick={() => setCategoryId(c.id)}
                title={c.name}
              >
                {c.name}
              </Chip>
            ))}
          </div>

          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="w-14 shrink-0 text-[10px] uppercase tracking-widest text-ink-4">
              Status
            </span>
            <Chip sm active={status === 'all'} onClick={() => setStatus('all')}>
              All
            </Chip>
            <Chip sm active={status === 'active'} onClick={() => setStatus('active')}>
              Active
            </Chip>
            <Chip sm active={status === 'inactive'} onClick={() => setStatus('inactive')}>
              Inactive
            </Chip>
            <span className="mx-1 h-4 w-px bg-rule shrink-0" />
            <Chip sm active={onSaleOnly} onClick={() => setOnSaleOnly((v) => !v)}>
              On promo
            </Chip>
            <Chip sm active={needsPricingOnly} onClick={() => setNeedsPricingOnly((v) => !v)}>
              Needs price{needsPricingCount > 0 ? ` (${needsPricingCount})` : ''}
            </Chip>
          </div>
        </div>
      </div>

      <DataTableCard
        scrollClassName="overflow-auto"
        footer={
          !loading && filtered.length > 0 ? (
            <Pagination
              page={safePage}
              pageCount={pageCount}
              total={filtered.length}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              unit="item"
            />
          ) : undefined
        }
      >
        {loading ? (
          <div className="px-6 py-12 text-center text-sm text-ink-3">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title={rows.length === 0 ? 'No products yet' : 'No products match'}
            description={
              rows.length === 0
                ? 'Add your first product to get started.'
                : 'Try a different filter or search.'
            }
            action={
              hasFilters ? (
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-y border-rule">
                <th className="px-6 py-2 font-medium w-16"></th>
                <th className="px-6 py-2 font-medium">Name</th>
                <th className="px-6 py-2 font-medium">Category</th>
                <th className="px-6 py-2 font-medium">Sizes</th>
                <th className="px-6 py-2 font-medium text-right">Price</th>
                <th className="px-6 py-2 font-medium text-right">Promo</th>
                <th className="px-6 py-2 font-medium text-right">Stock</th>
                <th className="px-6 py-2 font-medium">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {paged.map((r) => (
                <tr
                  key={`${r.kind}-${r.id}`}
                  className={cn(
                    'border-b border-rule-2 group hover:bg-paper-2',
                    !r.isActive && 'opacity-50',
                  )}
                >
                  <td className="px-6 py-3">
                    {r.image ? (
                      <img
                        src={r.image}
                        alt=""
                        className="w-10 h-10 rounded object-cover border border-rule"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-paper-2 border border-rule flex items-center justify-center text-ink-4 text-[10px]">
                        no img
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <div className={cn('text-ink', !r.isActive && 'line-through decoration-ink-3')}>
                      {r.name}
                    </div>
                    <div className="text-xs text-ink-3 kc-mono">
                      {r.kind === 'product' ? r.product.sku : 'model'}
                      {!r.isActive && (
                        <span className="ml-2 inline-block rounded bg-rule px-1.5 py-px text-[10px] uppercase tracking-wide text-ink-3">
                          deactivated
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    {/* Category NAME (not code). Long names truncate with an
                        ellipsis and reveal in full on hover via the title. */}
                    <span
                      title={r.category.name}
                      className="inline-block max-w-[12rem] truncate rounded border border-rule bg-paper-2 px-2 py-0.5 align-middle text-xs font-medium text-ink-3"
                    >
                      {r.category.name}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-ink-3">
                    {r.kind === 'model' ? (
                      `${r.model.variantCount} size${r.model.variantCount === 1 ? '' : 's'}`
                    ) : (
                      <span className="text-ink-4">—</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {r.needsPricing && r.price <= 0 ? (
                      <StatusPill tone="warning">needs price</StatusPill>
                    ) : r.kind === 'model' ? (
                      <PriceRange from={r.model.priceFrom} to={r.model.priceTo} />
                    ) : (
                      <Money value={r.product.basePrice} />
                    )}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {r.kind === 'product' ? (
                      r.product.salePrice ? (
                        <Money value={r.product.salePrice} className="text-accent" />
                      ) : (
                        <span className="text-ink-4">—</span>
                      )
                    ) : r.onSale ? (
                      <StatusPill tone="accent">on promo</StatusPill>
                    ) : (
                      <span className="text-ink-4">—</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right kc-mono kc-tnum text-ink-2">
                    {r.kind === 'model' ? (r.model.totalStock ?? '—') : '—'}
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill tone={r.isActive ? 'positive' : 'neutral'}>
                      {r.isActive ? 'active' : 'inactive'}
                    </StatusPill>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex gap-1 justify-end opacity-60 group-hover:opacity-100 transition">
                      {r.kind === 'model' ? (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setModelImagesFor(r.id)}>
                            Images
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditModelId(r.id)}>
                            Edit
                          </Button>
                          {r.isActive ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deactivateModel.mutate(r.id)}
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => reactivateModel.mutate(r.id)}
                            >
                              Reactivate
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setProductImagesFor(r.id)}
                          >
                            Images
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(r.product)}>
                            Edit
                          </Button>
                          {r.isActive ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deactivateProduct.mutate(r.id)}
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => reactivateProduct.mutate(r.id)}
                            >
                              Reactivate
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableCard>

      {(wizardOpen || resumeId || editModelId) && (
        <ProductModelWizard
          editModelId={resumeId ?? editModelId ?? undefined}
          startStep={resumeId ? 2 : 1}
          onClose={() => {
            setWizardOpen(false);
            setResumeId(null);
            setEditModelId(null);
          }}
        />
      )}
      {createOpen && (
        <ProductModal
          mode="create"
          categories={cats.data ?? []}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {editing && (
        <ProductModal
          mode="edit"
          product={editing}
          categories={cats.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
      {productImagesFor && (
        <ProductImagesModal
          productId={productImagesFor}
          onClose={() => setProductImagesFor(null)}
        />
      )}
      {modelImagesFor && (
        <ModelImagesModal modelId={modelImagesFor} onClose={() => setModelImagesFor(null)} />
      )}
    </PageScaffold>
  );
}

function PriceRange({ from, to }: { from: string | null; to: string | null }): JSX.Element {
  if (!from) return <span className="text-ink-4">—</span>;
  return (
    <span className="kc-tnum">
      <Money value={from} />
      {to && to !== from && (
        <>
          {' '}
          – <Money value={to} />
        </>
      )}
    </span>
  );
}

function ModelImagesModal({
  modelId,
  onClose,
}: {
  modelId: string;
  onClose: () => void;
}): JSX.Element {
  const model = useQuery({
    queryKey: ['product-model', modelId],
    queryFn: () =>
      apiGet<{ name: string; images: { id: string; url: string; isPrimary: boolean }[] }>(
        `/product-models/${modelId}`,
      ),
  });
  return (
    <Modal
      title={`Images · ${model.data?.name ?? '…'}`}
      size="lg"
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      {model.data ? (
        <ModelImagesManager modelId={modelId} images={model.data.images} />
      ) : (
        <div className="text-sm text-ink-3">Loading…</div>
      )}
    </Modal>
  );
}


function Chip({
  active,
  onClick,
  children,
  title,
  sm,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
  sm?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'rounded text-xs uppercase tracking-wide transition border',
        sm ? 'px-2 py-0.5' : 'px-3 py-1.5',
        active
          ? 'bg-accent text-white border-accent'
          : 'bg-paper-2 text-ink-2 border-rule hover:border-accent/40',
      )}
    >
      {children}
    </button>
  );
}

function ProductModal({
  mode,
  product,
  categories,
  onClose,
}: {
  mode: 'create' | 'edit';
  product?: Product;
  categories: Category[];
  onClose: () => void;
}): JSX.Element {
  const [sku, setSku] = useState(product?.sku ?? '');
  const [name, setName] = useState(product?.name ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [categoryId, setCategoryId] = useState(product?.category.id ?? '');
  const [basePrice, setBasePrice] = useState(product?.basePrice ?? '');
  const [salePrice, setSalePrice] = useState(product?.salePrice ?? '');
  const [vatRate, setVatRate] = useState(String(product ? Number(product.vatRate) * 100 : 15));
  const [stockByLoc, setStockByLoc] = useState<Record<string, string>>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const qc = useQueryClient();

  // Revoke the preview object URL when it changes or the modal unmounts.
  useEffect(
    () => () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    },
    [imagePreview],
  );
  const pickImage = (file: File | null): void => {
    setImageFile(file);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };

  // Opening-stock entry on the catalogue is gated by stock.adjust. Roles without
  // it (e.g. ops) create the product but set stock from the Stock page instead.
  const canAdjustStock = useHasPermission('stock.adjust');
  const selectedCat = categories.find((c) => c.id === categoryId);
  const stockTracked = (selectedCat?.tracksStockPerLocation ?? false) && canAdjustStock;
  const locations = useQuery({
    queryKey: ['stock-locations'],
    queryFn: () =>
      apiGet<
        Array<{
          id: string;
          name: string;
          kind: string;
          shop: { code: string; name: string } | null;
        }>
      >('/stock/locations'),
    enabled: mode === 'create' && canAdjustStock,
  });

  // Suggest the next category-prefixed running code (e.g. BED-00042), shared with
  // the model wizard's "Auto-fill codes". categoryId is passed in (not read from
  // closure) so the just-picked category is used even before state settles.
  const autoSku = useMutation({
    mutationFn: async (catId: string): Promise<string> => {
      const { codes } = await apiGet<{ prefix: string; nextNumber: number; codes: string[] }>(
        '/product-models/next-skus',
        { categoryId: catId, count: 1 },
      );
      return codes[0] ?? '';
    },
    onSuccess: (code) => {
      if (code) setSku(code);
    },
  });
  // On create, pick a category → auto-fill a blank SKU. Manual entries are kept.
  const pickCategory = (id: string): void => {
    setCategoryId(id);
    if (mode === 'create' && id && !sku.trim()) autoSku.mutate(id);
  };

  const submit = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name,
        description: description || null,
        categoryId,
        basePrice: Number(basePrice),
        salePrice: salePrice ? Number(salePrice) : null,
        vatRate: Number(vatRate) / 100,
      };
      if (mode === 'edit') {
        return apiPatch(`/products/${product!.id}`, body);
      }
      body.sku = sku;
      if (stockTracked) {
        const entries = Object.entries(stockByLoc)
          .map(([locationId, v]) => ({ locationId, qty: Number(v) }))
          .filter((e) => Number.isFinite(e.qty) && e.qty > 0);
        if (entries.length) body.openingStock = entries;
      }
      const created = await apiPost<{ id: string; modelId?: string | null }>('/products', body);
      // Image upload needs the product's id, so it happens after create: the bytes
      // go straight to storage via a presigned URL, then we register it on the
      // product AND on its auto-created model — the catalogue card shows the model
      // image — reusing the same uploaded URL (no second upload).
      if (imageFile) {
        const url = await uploadProductImage(created.id, imageFile);
        await apiPost(`/products/${created.id}/images`, { url, isPrimary: true });
        if (created.modelId) await apiPost(`/product-models/${created.modelId}/images`, { url, isPrimary: true });
      }
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products-admin'] });
      qc.invalidateQueries({ queryKey: ['products'] });
      onClose();
    },
  });

  const disabled =
    mode === 'create'
      ? !sku || !name || !categoryId || !basePrice
      : !name || !categoryId || !basePrice;

  return (
    <Modal
      title={mode === 'create' ? 'New product' : `Edit · ${product?.sku}`}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={submit.isPending} disabled={disabled} onClick={() => submit.mutate()}>
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">SKU</span>
              {mode === 'create' && (
                <button
                  type="button"
                  disabled={!categoryId || autoSku.isPending}
                  onClick={() => autoSku.mutate(categoryId)}
                  className="text-[11px] text-accent hover:underline disabled:text-ink-4 disabled:no-underline"
                  title={
                    !categoryId
                      ? 'Pick a category first'
                      : 'Generate the next running code (e.g. BED-00042)'
                  }
                >
                  {autoSku.isPending ? 'Generating…' : 'Auto-generate'}
                </button>
              )}
            </span>
            <input
              className="kc-input kc-mono"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              required
              disabled={mode === 'edit'}
              placeholder="e.g. BED-00042"
            />
            {mode === 'edit' && <span className="text-xs text-ink-3">SKU is permanent</span>}
          </label>
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="col-span-2"
          />
        </div>
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Category</span>
          <select
            value={categoryId}
            onChange={(e) => pickCategory(e.target.value)}
            className="kc-input"
          >
            <option value="">— pick category —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <Input
          label="VAT %"
          type="number"
          step="0.5"
          min="0"
          max="100"
          value={vatRate}
          onChange={(e) => setVatRate(e.target.value)}
          hint="Used to convert between excl. and incl. VAT prices."
        />
        <div>
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
            Base price MUR
          </span>
          <VatPricePair
            rate={Number(vatRate) / 100}
            valueIncl={String(basePrice)}
            onChangeIncl={setBasePrice}
          />
        </div>
        <div>
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
            Promo price MUR (optional)
          </span>
          <VatPricePair
            rate={Number(vatRate) / 100}
            valueIncl={String(salePrice)}
            onChangeIncl={setSalePrice}
            placeholder="—"
          />
          <span className="text-[11px] text-ink-3 mt-1 block">
            Leave empty for no promo. The incl.-VAT price is what the customer pays.
          </span>
        </div>
        {mode === 'create' && (
          <div>
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
              Image (optional)
            </span>
            <div className="mt-1 flex items-center gap-3">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt=""
                  className="w-16 h-16 rounded object-cover border border-rule"
                />
              ) : (
                <div className="w-16 h-16 rounded bg-paper-2 border border-rule flex items-center justify-center text-ink-4 text-[10px]">
                  no img
                </div>
              )}
              <div className="space-y-1">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/avif,image/gif,image/svg+xml"
                  onChange={(e) => pickImage(e.target.files?.[0] ?? null)}
                  className="block text-sm text-ink-2 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-accent file:text-white file:cursor-pointer hover:file:bg-accent-2 file:transition"
                />
                <div className="text-[11px] text-ink-3">
                  {imageFile ? (
                    <Button
                      type="button"
                      variant="tertiary-danger"
                      size="xs"
                      onClick={() => pickImage(null)}
                    >
                      Remove
                    </Button>
                  ) : (
                    'Set as the primary image. You can add more later from the Images button.'
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {mode === 'edit' && product && (
          <div>
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Images</span>
            <div className="mt-1">
              <ProductImagesManager productId={product.id} />
            </div>
          </div>
        )}
        {mode === 'create' && stockTracked && (
          <div>
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
              Opening stock (optional)
            </span>
            {(locations.data ?? []).length === 0 ? (
              <div className="text-xs text-ink-3 mt-1">
                No stock locations yet — create a shop or warehouse first.
              </div>
            ) : (
              <div className="mt-2 space-y-1.5">
                {(locations.data ?? []).map((loc) => (
                  <div key={loc.id} className="flex items-center gap-3">
                    <span className="text-sm text-ink-2 flex-1">
                      {loc.shop ? `${loc.shop.code} · ${loc.shop.name}` : loc.name}
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-ink-4">
                        {loc.kind}
                      </span>
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="kc-input w-24 text-right"
                      placeholder="0"
                      value={stockByLoc[loc.id] ?? ''}
                      onChange={(e) => setStockByLoc((s) => ({ ...s, [loc.id]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="text-xs text-ink-3 mt-1">Initial quantities held at each location.</div>
          </div>
        )}
        {autoSku.isError && (
          <div className="text-xs text-negative">{getApiError(autoSku.error).message}</div>
        )}
        {submit.isError && (
          <div className="text-xs text-negative">{getApiError(submit.error).message}</div>
        )}
      </div>
    </Modal>
  );
}
